/**
 * The Availability grid's view model — pure, so changing Group by, Sort or a
 * filter recomputes in the browser and never refetches.
 *
 * The server returns classified cells; this adapts those rows to the shapes the
 * pure rules in `@/lib/availability/` take (a payload cell already carries every
 * field of both `AvailabilityCellInput` and `CellResult`), then runs grouping,
 * sorting, tiles and filters over them.
 *
 * 🔒 FIREWALL: hours only. No Fit Score, Capacity, Intensity or points.
 */
import { isFreeState, isOverState } from "@/lib/availability/cell-state";
import {
  type GroupCellResult,
  groupCell,
  groupRows,
  hasPlan,
  sortRows,
  windowNetHours,
} from "@/lib/availability/groups";
import { addMonths, monthDiff, monthLabel, monthStartOf } from "@/lib/availability/months";
import { type AvailabilityTiles, computeTiles, tileFilter } from "@/lib/availability/tiles";
import type {
  AvailabilityCellPayload,
  AvailabilityPayload,
  AvailabilityResourcePayload,
} from "@/schemas/availability";
import { formatHours } from "./labels";
import type { AvailabilityView, ReportTile } from "./search-params";

/** More Resources than this and groups load collapsed (search still opens the matches). */
export const COLLAPSE_ABOVE = 150;

/** One person: the payload row with its cells keyed by month (window AND actual months). */
export interface ViewRow extends Omit<AvailabilityResourcePayload, "cells" | "actualCells"> {
  cells: Record<string, AvailabilityCellPayload>;
}

/** Adapt payload rows (cell arrays) to month-keyed rows the lib rules take. */
export function toViewRows(resources: readonly AvailabilityResourcePayload[]): ViewRow[] {
  return resources.map((resource) => {
    const { cells, actualCells, ...rest } = resource;
    const byMonth: Record<string, AvailabilityCellPayload> = {};
    for (const cell of actualCells) byMonth[cell.month] = cell;
    for (const cell of cells) byMonth[cell.month] = cell;
    return { ...rest, cells: byMonth };
  });
}

/** The group id used for open/closed bookkeeping; Unassigned has a null key. */
export function groupId(key: string | null): string {
  return key ?? "__unassigned__";
}

export interface GridGroup {
  /** Stable id for expand/collapse. */
  id: string;
  /** The raw grouping value, null for Unassigned. */
  key: string | null;
  label: string;
  isUnassigned: boolean;
  /** Everyone in the group after Delivery-roles and search (the roll-up covers all of them). */
  members: ViewRow[];
  /** Members that pass the tile and Exceptions filters, sorted. */
  rows: ViewRow[];
  hiddenCount: number;
  /** One per column month. */
  cells: GroupCellResult[];
  /** One per "Last 3 mo" month (empty when that column is hidden). */
  lastThree: GroupCellResult[];
  /** Σ over the window of Σ(Available − Booked) over planned months; positive = free. */
  netHours: number;
}

export interface GridModel {
  /** Rows after Delivery-roles and search: what tiles and group totals cover. */
  base: ViewRow[];
  tiles: AvailabilityTiles;
  /** Free hours per Home Role (top three) for the first tile's sub-line. */
  topFreeRoles: Array<{ role: string; hours: number }>;
  /** Names for the Rolling off tile's sub-line. */
  rollingOffNames: string[];
  groups: GridGroup[];
  /** Rows shown across every group. */
  visibleCount: number;
}

export interface GridInput {
  rows: readonly ViewRow[];
  view: AvailabilityView;
  payload: Pick<AvailabilityPayload, "months" | "cutoffs" | "planHorizon">;
  /** The months the grid shows, window plus any prepended actual months. */
  columnMonths: readonly string[];
  /** The months behind "Last 3 mo"; empty when that column is hidden. */
  lastThreeMonths?: readonly string[];
}

/** True when the person is Fully booked (or has nothing to compare) in every window month. */
export function isNeutralRow(row: ViewRow, windowMonths: readonly string[]): boolean {
  return windowMonths.every((month) => {
    const state = row.cells[month]?.state;
    return (
      state === undefined || state === "fullyBooked" || state === "noData" || state === "unplanned"
    );
  });
}

/** The row predicate a tile applies. Data gaps = no Home Role OR an open issue. */
export function tilePredicate(
  tile: ReportTile,
  windowMonths: readonly string[],
): (row: ViewRow) => boolean {
  switch (tile) {
    case "bookableFree":
      return tileFilter("bookableFree", windowMonths);
    case "overUtilized":
      return tileFilter("overUtilized", windowMonths);
    case "rollingOff":
      return tileFilter("rollingOff", windowMonths);
    case "dataGaps": {
      const noRole = tileFilter("noHomeRole", windowMonths);
      const issues = tileFilter("issues", windowMonths);
      return (row) => noRole(row) || issues(row);
    }
  }
}

function matchesSearch(row: ViewRow, query: string): boolean {
  if (query === "") return true;
  return row.name.toLowerCase().includes(query.toLowerCase());
}

function topFreeRoles(rows: readonly ViewRow[], windowMonths: readonly string[], limit: number) {
  const hoursByRole = new Map<string, number>();
  for (const row of rows) {
    const role = row.homeRole?.trim() ? row.homeRole.trim() : "No Home Role";
    for (const month of windowMonths) {
      const cell = row.cells[month];
      if (cell !== undefined && isFreeState(cell.state) && cell.netHours > 0) {
        hoursByRole.set(role, (hoursByRole.get(role) ?? 0) + cell.netHours);
      }
    }
  }
  return [...hoursByRole.entries()]
    .map(([role, hours]) => ({ role, hours }))
    .sort((a, b) => b.hours - a.hours || a.role.localeCompare(b.role))
    .slice(0, limit);
}

export function buildGrid(input: GridInput): GridModel {
  const { rows, view, payload, columnMonths } = input;
  const lastThreeMonths = input.lastThreeMonths ?? [];
  const windowMonths = payload.months;

  const base = rows.filter(
    (row) => (!view.deliveryOnly || !row.nonDelivery) && matchesSearch(row, view.q),
  );
  const tiles = computeTiles(base, windowMonths, payload.planHorizon);
  const rollingOffNames = base.filter((row) => row.rollingOff).map((row) => row.name);

  const passesTile = view.tile === null ? null : tilePredicate(view.tile, windowMonths);
  const passes = (row: ViewRow): boolean => {
    if (passesTile !== null && !passesTile(row)) return false;
    if (view.exceptionsOnly) {
      return !isNeutralRow(row, windowMonths) || row.chronic || row.hasIssue;
    }
    return true;
  };
  const filtering = view.tile !== null || view.q !== "" || view.exceptionsOnly;

  const groups: GridGroup[] = [];
  let visibleCount = 0;
  for (const group of groupRows(base, view.groupBy)) {
    const shown = sortRows(group.rows.filter(passes), view.sort, windowMonths);
    // A filter that empties a group hides it; with no filter an empty group cannot exist.
    if (shown.length === 0 && filtering) continue;
    visibleCount += shown.length;
    let netHours = 0;
    for (const member of group.rows) netHours += windowNetHours(member, windowMonths);
    groups.push({
      id: groupId(group.key),
      key: group.key,
      label: group.label,
      isUnassigned: group.isUnassigned,
      members: group.rows,
      rows: shown,
      hiddenCount: group.rows.length - shown.length,
      cells: columnMonths.map((month) => groupCell(group.rows, month, payload.cutoffs)),
      lastThree: lastThreeMonths.map((month) => groupCell(group.rows, month, payload.cutoffs)),
      netHours: Math.round(netHours * 10) / 10,
    });
  }

  return {
    base,
    tiles,
    topFreeRoles: topFreeRoles(base, windowMonths, 3),
    rollingOffNames,
    groups,
    visibleCount,
  };
}

/** Months the grid shows: the window, with the three actual months in front when asked for. */
export function columnMonthsFor(
  payload: Pick<AvailabilityPayload, "months" | "actualMonths" | "today">,
  showActual: boolean,
): string[] {
  const first = payload.months[0];
  const current = monthStartOf(payload.today);
  // Only a window that starts THIS month has three actual months in front of it: an
  // earlier start already shows its own, and a later one's "previous" months are
  // forecasts, not actuals.
  if (showActual && first !== undefined && monthDiff(current, first) === 0) {
    return [...payload.actualMonths, ...payload.months];
  }
  return [...payload.months];
}

/** "Last 3 mo" shows when the window starts this month and actual columns are off. */
export function showLastThree(
  payload: Pick<AvailabilityPayload, "months" | "actualMonths" | "today">,
  showActual: boolean,
): boolean {
  const first = payload.months[0];
  if (first === undefined || showActual || payload.actualMonths.length === 0) return false;
  return monthDiff(monthStartOf(payload.today), first) === 0;
}

export type MonthHeaderKind = "actual" | "mtd" | "forecast" | "partial";

/** The small sub-label under a month header: ACTUAL, MTD, FORECAST, or PARTIAL past the horizon. */
export function monthHeaderKind(
  month: string,
  today: string,
  planHorizon: string | null,
): MonthHeaderKind {
  const diff = monthDiff(monthStartOf(today), month);
  if (diff < 0) return "actual";
  if (diff === 0) return "mtd";
  return planHorizon === null || month > planHorizon ? "partial" : "forecast";
}

/** The month that gets the dashed line before it: the first one past the plan horizon. */
export function horizonEdgeMonth(planHorizon: string | null): string | null {
  return planHorizon === null ? null : addMonths(planHorizon, 1);
}

/** Booked ÷ Available as a bar height ratio, capped at 150%. 0 when there is nothing to show. */
export function barRatio(cell: { pct: number | null } | undefined): number {
  if (cell === undefined || cell.pct === null) return 0;
  return Math.min(Math.max(cell.pct, 0), 150) / 150;
}

/** The average Booked % over the cells that have one, e.g. `"92%"`, or `"–"`. */
export function averagePct(cells: ReadonlyArray<{ pct: number | null } | undefined>): string {
  const values: number[] = [];
  for (const cell of cells) {
    if (cell !== undefined && cell.pct !== null) values.push(cell.pct);
  }
  if (values.length === 0) return "–";
  return `${Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)}%`;
}

/**
 * Six consecutive months around `selected` for the panel's trend, from the months
 * the row has. Starts two months before the selected one, clamped to what exists.
 */
export function trendMonths(allMonths: readonly string[], selected: string, count = 6): string[] {
  const sorted = [...allMonths].sort();
  const index = sorted.indexOf(selected);
  if (index < 0) return sorted.slice(0, count);
  const start = Math.max(0, Math.min(index - 2, sorted.length - count));
  return sorted.slice(start, start + count);
}

export interface GroupMonthTotals {
  /** Members in the group. */
  total: number;
  /** Members with a plan for the month (the roll-up covers only these). */
  planned: number;
  /** Σ Available Hours over planned members. */
  availableHours: number;
  /** Σ Booked Hours over planned members. */
  bookedHours: number;
  /** Members showing a "+N" (Over-utilized or Stretched) and members with room. */
  overCount: number;
  roomCount: number;
}

/** The numbers behind a group cell, for its tooltip and panel. Same rule as `groupCell`. */
export function groupMonthTotals(members: readonly ViewRow[], month: string): GroupMonthTotals {
  const totals: GroupMonthTotals = {
    total: members.length,
    planned: 0,
    availableHours: 0,
    bookedHours: 0,
    overCount: 0,
    roomCount: 0,
  };
  for (const member of members) {
    const cell = member.cells[month];
    if (cell === undefined) continue;
    if (isOverState(cell.state)) totals.overCount++;
    else if (isFreeState(cell.state)) totals.roomCount++;
    if (hasPlan(cell)) {
      totals.planned++;
      totals.availableHours += cell.availableHours ?? 0;
      totals.bookedHours += cell.bookedHours;
    }
  }
  return totals;
}

export interface TileCard {
  id: ReportTile;
  /** The eyebrow label on the tile. */
  label: string;
  value: string;
  sub: string;
  tone: "free" | "over" | "neutral" | "gap";
  /** What the active-filter chip says. */
  filterLabel: string;
}

function planHorizonText(planHorizon: string | null): string {
  if (planHorizon === null) return "No plan horizon";
  return `Plan horizon ${monthLabel(planHorizon, { short: true })} '${planHorizon.slice(2, 4)}`;
}

/**
 * The four headline tiles. Each is also a filter, so every number names the people
 * it counts. Numbers come from `computeTiles` over the same rows the grid shows.
 */
export function tileCards(
  grid: Pick<GridModel, "tiles" | "topFreeRoles" | "rollingOffNames" | "base">,
  planHorizon: string | null,
  windowMonths: readonly string[],
): TileCard[] {
  const { tiles } = grid;
  const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;
  const rolling = grid.rollingOffNames;
  const gaps = grid.base.filter(tilePredicate("dataGaps", windowMonths)).length;
  return [
    {
      id: "bookableFree",
      label: "BOOKABLE FREE HOURS",
      value: `${formatHours(tiles.bookableFreeHours)} h`,
      sub:
        grid.topFreeRoles.length > 0
          ? grid.topFreeRoles.map((r) => `${r.role} ${formatHours(r.hours)}`).join(" · ")
          : "No room in this window",
      tone: "free",
      filterLabel: "Showing people with room",
    },
    {
      id: "overUtilized",
      label: "OVER-UTILIZED",
      value: people(tiles.overUtilized.people),
      sub: `${tiles.overUtilized.chronic} chronic · ${formatHours(tiles.overUtilized.hoursOver)} h over in window`,
      tone: "over",
      filterLabel: "Showing over-utilized",
    },
    {
      id: "rollingOff",
      label: "ROLLING OFF IN 60 DAYS",
      value: people(tiles.rollingOff),
      sub:
        rolling.length === 0
          ? "Nobody rolling off"
          : `${rolling.slice(0, 2).join(", ")}${
              rolling.length > 2 ? ` and ${rolling.length - 2} more` : ""
            }`,
      tone: "neutral",
      filterLabel: "Showing rolling off",
    },
    {
      id: "dataGaps",
      label: "DATA GAPS",
      value: String(gaps),
      sub: `${tiles.dataGaps.noHomeRole} no Home Role · ${tiles.dataGaps.issues} with issues · ${planHorizonText(planHorizon)}`,
      tone: "gap",
      filterLabel: "Showing data gaps",
    },
  ];
}
